"use client";

import { useState } from "react";
import TestimonialCard from "@/HomeFeatures/Testimonial/TestimonialCard";
import { getTestimonialList } from "@/lib/ApiClient";
import { mapTestimonialsArray } from "@/lib/Utility";
import Loader from "@/components/Loader";
import { TestimonialItem } from "@/lib/Types";

const LIMIT = 6;

interface TestimonialsGridProps {
  initialTestimonials: TestimonialItem[];
}

/**
 * Client grid for /testimonial. Page 1 is fetched (and shuffled) on the server
 * and passed in, so the first cards are part of the prerendered HTML; this
 * component only fetches subsequent pages on "Load More".
 */
export default function TestimonialsGrid({
  initialTestimonials,
}: TestimonialsGridProps) {
  const [testimonials, setTestimonials] =
    useState<TestimonialItem[]>(initialTestimonials);
  const [currentPage, setCurrentPage] = useState<number>(
    initialTestimonials.length > 0 ? 1 : 0,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // --- Utils ---
  function shuffleArray<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  // --- API Call ---
  const fetchTestimonialList = async (page: number): Promise<void> => {
    setIsLoading(true);

    try {
      const response: any = await getTestimonialList(page, LIMIT);
      const newItems = mapTestimonialsArray(response?.items ?? []);
      const shuffledItems = shuffleArray(newItems);
      setTestimonials((prev: any) => [...prev, ...shuffledItems]);
      setCurrentPage(page); // ✅ keep source of truth here
    } catch (err) {
      console.error(
        err instanceof Error ? err.message : "Failed to load testimonials",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // --- Load More ---
  const loadMore = (): void => {
    if (isLoading) return;

    const nextPage = currentPage + 1;
    void fetchTestimonialList(nextPage);
  };

  // --- Helpers ---
  const toUrl = (img: string | { src: string }): string =>
    typeof img === "string" ? img : img?.src ?? "";

  return (
    <>
      <div className="max-w-[2500px] mx-auto w-[calc(100vw_-_10%)] min-h-[400px]">
        {isLoading && testimonials.length === 0 ? (
          <div className="flex justify-center items-center w-full py-10">
            <Loader size={80} />
          </div>
        ) : testimonials.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 px-5">
            {testimonials?.map((item, idx) => {
              return (
                <div key={item.id ?? `${item.companyName}-${idx}`}>
                  <TestimonialCard
                    {...item}
                    companyLogoSrc={toUrl(item.companyLogoSrc || "")}
                    ratingBadgeSrc={toUrl(item.ratingBadgeSrc || "")}
                    authorAvatarSrc={toUrl(item.authorAvatarSrc)}
                    clientVideo={item?.clientVideo ?? undefined}
                    surveyId={item?.id ?? 0}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex justify-center items-center w-full py-10 text-gray-500">
            <p>No Testimonials Found.</p>
          </div>
        )}
      </div>

      <div className="text-center mt-16 flex justify-center ">
        <button
          onClick={loadMore}
          disabled={isLoading}
          className="h-[68px] px-10 bg-white rounded-[68px] shadow-lg
                     flex items-center justify-center gap-3 text-lg font-semibold
                     hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all"
        >
          {isLoading ? "Loading..." : "Load More Testimonials"}
        </button>
      </div>
    </>
  );
}
